import React, { useCallback, useRef, useState, createContext, useContext } from 'react';
import { useEditor, EditorContent, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { Node, mergeAttributes, nodeInputRule, nodePasteRule } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { Underline } from '@tiptap/extension-underline';
import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { FontFamily } from '@tiptap/extension-font-family';
import { FontSize } from '@tiptap/extension-font-size';
import { Highlight } from '@tiptap/extension-highlight';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import { Image } from '@tiptap/extension-image';
import { Link } from '@tiptap/extension-link';
import { Placeholder } from '@tiptap/extension-placeholder';
import { CharacterCount } from '@tiptap/extension-character-count';
import {
    Box, IconButton, Divider, Tooltip, Select, MenuItem, Popover, Typography,
    ToggleButton, ToggleButtonGroup, Switch, FormControlLabel,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button,
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import TableChartIcon from '@mui/icons-material/TableChart';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import TableRowsIcon from '@mui/icons-material/TableRows';
import BorderClearIcon from '@mui/icons-material/BorderClear';
import BorderAllIcon from '@mui/icons-material/BorderAll';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import ImageIcon from '@mui/icons-material/Image';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import DataObjectIcon from '@mui/icons-material/DataObject';

const EditorExtrasContext = createContext({ logoUrl: null });

// ─── Constantes ────────────────────────────────────────────────────────────────

const FONT_SIZES = ['8', '9', '10', '11', '12', '14', '16', '18', '20', '24', '28', '32', '36', '48', '72'];

const FONT_FAMILIES = [
    { label: 'Times New Roman', value: '"Times New Roman", Times, serif' },
    { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
    { label: 'Calibri', value: 'Calibri, sans-serif' },
    { label: 'Georgia', value: 'Georgia, serif' },
    { label: 'Courier New', value: '"Courier New", Courier, monospace' },
];

const COLORS = [
    { label: 'Preto', value: '#000000' },
    { label: 'Cinza', value: '#555555' },
    { label: 'Azul', value: '#1565C0' },
    { label: 'Verde', value: '#2E7D32' },
    { label: 'Vermelho', value: '#C62828' },
    { label: 'Roxo', value: '#6A1B9A' },
    { label: 'Laranja', value: '#E65100' },
];

const HIGHLIGHTS = [
    { label: 'Nenhum', value: 'none' },
    { label: 'Amarelo', value: '#FFF176' },
    { label: 'Verde', value: '#C8E6C9' },
    { label: 'Azul', value: '#BBDEFB' },
    { label: 'Rosa', value: '#F8BBD0' },
    { label: 'Laranja', value: '#FFE0B2' },
];

const GRID_COLS = 8;
const GRID_ROWS = 8;

const VARIAVEIS = [
    {
        grupo: 'Paciente',
        itens: [
            { v: 'NOME_PACIENTE', l: 'Nome' },
            { v: 'CPF_PACIENTE', l: 'CPF' },
            { v: 'RG', l: 'RG' },
            { v: 'PROFISSAO', l: 'Profissão' },
            { v: 'ESTADO_CIVIL', l: 'Estado Civil' },
            { v: 'NACIONALIDADE', l: 'Nacionalidade' },
            { v: 'ENDERECO_PACIENTE', l: 'Endereço' },
            { v: 'CIDADE_PACIENTE', l: 'Cidade' },
            { v: 'UF_PACIENTE', l: 'UF' },
            { v: 'CIDADE_UF_PACIENTE', l: 'Cidade/UF' },
            { v: 'CEP_PACIENTE', l: 'CEP' },
        ],
    },
    {
        grupo: 'Psicólogo',
        itens: [
            { v: 'NOME_PSICOLOGO', l: 'Nome' },
            { v: 'CPF_PSICOLOGO', l: 'CPF' },
            { v: 'CRP_PSICOLOGO', l: 'CRP' },
        ],
    },
    {
        grupo: 'Consultório',
        itens: [
            { v: 'NOME_CONSULTORIO', l: 'Nome' },
            { v: 'ENDERECO_CONSULTORIO', l: 'Endereço' },
            { v: 'CIDADE_CONSULTORIO', l: 'Cidade' },
            { v: 'UF_CONSULTORIO', l: 'UF' },
            { v: 'CIDADE_UF_CONSULTORIO', l: 'Cidade/UF' },
            { v: 'TELEFONE_CONSULTORIO', l: 'Telefone' },
        ],
    },
    {
        grupo: 'Assinatura/Plano',
        itens: [
            { v: 'NOME_PLANO', l: 'Nome do plano' },
            { v: 'VALOR_SESSAO', l: 'Valor da sessão' },
            { v: 'FORMA_PAGAMENTO', l: 'Forma de pagamento' },
            { v: 'DATA_INICIO_ASSINATURA', l: 'Início' },
            { v: 'DATA_FIM_ASSINATURA', l: 'Fim' },
        ],
    },
    {
        grupo: 'Data',
        itens: [
            { v: 'DATA_ATUAL', l: 'Data atual (dd/MM/yyyy)' },
            { v: 'DATA_EXTENSO', l: 'Data por extenso' },
            { v: 'DATA_REFERENCIA', l: 'Data de referência' },
        ],
    },
    {
        // Preenchidos na emissão, não pelo cadastro: só fazem sentido nos
        // modelos de declaração, encaminhamento e pedido médico.
        grupo: 'Emissão',
        itens: [
            { v: 'NUMERO_DOCUMENTO', l: 'Número do documento' },
            { v: 'DESTINATARIO', l: 'Destinatário (encaminhamento/pedido)' },
            { v: 'MOTIVO', l: 'Motivo / solicitação' },
            { v: 'FINALIDADE', l: 'Finalidade (declaração)' },
        ],
    },
];

// ─── Imagem redimensionável e arrastável ──────────────────────────────────────

function ResizableImageNodeView({ node, updateAttributes, selected, editor }) {
    const { src, width, align } = node.attrs;
    const containerRef = useRef(null);

    const justifyMap = { left: 'flex-start', center: 'center', right: 'flex-end' };

    const startResize = (side) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startW = containerRef.current?.offsetWidth || 200;
        const onMove = (ev) => {
            const dx = ev.clientX - startX;
            const delta = side === 'left' ? -dx : dx;
            const newW = Math.max(40, Math.min(2000, startW + delta));
            updateAttributes({ width: Math.round(newW) });
        };
        const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    const setAlign = (v) => updateAttributes({ align: v });
    const remove = () => editor?.chain().focus().deleteSelection().run();

    const handleSx = {
        position: 'absolute', width: 10, height: 10,
        bgcolor: '#1ABB9C', border: '2px solid #fff', borderRadius: '50%',
        zIndex: 10,
    };

    return (
        <NodeViewWrapper as="div" style={{ display: 'flex', justifyContent: justifyMap[align] || 'flex-start' }}>
            <Box
                ref={containerRef}
                data-drag-handle
                draggable
                sx={{
                    position: 'relative',
                    display: 'inline-block',
                    maxWidth: '100%',
                    width: width ? `${width}px` : 'auto',
                    border: selected ? '2px solid #1ABB9C' : '2px solid transparent',
                    cursor: selected ? 'grab' : 'default',
                    '&:hover': { border: selected ? '2px solid #1ABB9C' : '2px solid #e0e0e0' },
                }}
            >
                <Box component="img" src={src} draggable={false}
                    sx={{ display: 'block', width: '100%', height: 'auto', userSelect: 'none' }} />

                {selected && (
                    <>
                        <Box onMouseDown={startResize('right')} sx={{ ...handleSx, right: -6, top: '50%', transform: 'translateY(-50%)', cursor: 'ew-resize' }} />
                        <Box onMouseDown={startResize('left')}  sx={{ ...handleSx, left: -6, top: '50%', transform: 'translateY(-50%)', cursor: 'ew-resize' }} />
                        <Box onMouseDown={startResize('right')} sx={{ ...handleSx, right: -6, bottom: -6, cursor: 'nwse-resize' }} />
                        <Box onMouseDown={startResize('left')}  sx={{ ...handleSx, left: -6, bottom: -6, cursor: 'nesw-resize' }} />

                        <Box sx={{
                            position: 'absolute', top: -38, left: '50%', transform: 'translateX(-50%)',
                            display: 'flex', alignItems: 'center', gap: 0.5,
                            bgcolor: '#fff', border: '1px solid #ddd', borderRadius: 1,
                            px: 0.5, py: 0.3, boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                            zIndex: 11, whiteSpace: 'nowrap',
                        }}>
                            <ToggleButtonGroup size="small" exclusive value={align || 'left'}
                                onChange={(_, v) => v && setAlign(v)}
                                sx={{ '& .MuiToggleButton-root': { p: '2px 4px', border: 'none' } }}>
                                <ToggleButton value="left"><FormatAlignLeftIcon sx={{ fontSize: '0.9rem' }} /></ToggleButton>
                                <ToggleButton value="center"><FormatAlignCenterIcon sx={{ fontSize: '0.9rem' }} /></ToggleButton>
                                <ToggleButton value="right"><FormatAlignRightIcon sx={{ fontSize: '0.9rem' }} /></ToggleButton>
                            </ToggleButtonGroup>
                            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                            <Typography variant="caption" sx={{ color: '#666' }}>{width || 'auto'}px</Typography>
                            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                            <IconButton size="small" onClick={remove} sx={{ p: '2px', color: '#C62828' }}>
                                <RemoveIcon sx={{ fontSize: '0.9rem' }} />
                            </IconButton>
                        </Box>
                    </>
                )}
            </Box>
        </NodeViewWrapper>
    );
}

const ResizableImage = Image.extend({
    draggable: true,
    addAttributes() {
        return {
            ...this.parent?.(),
            width: { default: null, parseHTML: el => {
                const w = el.getAttribute('width') || el.style.width;
                if (!w) return null;
                const n = parseInt(w, 10);
                return Number.isFinite(n) ? n : null;
            }, renderHTML: attrs => attrs.width ? { width: attrs.width, style: `width:${attrs.width}px` } : {} },
            align: { default: 'left', parseHTML: el => el.getAttribute('data-align') || 'left',
                renderHTML: attrs => ({ 'data-align': attrs.align || 'left', style: `text-align:${attrs.align || 'left'}` }) },
        };
    },
    addNodeView() {
        return ReactNodeViewRenderer(ResizableImageNodeView);
    },
});

// ─── Extensão de tabela com suporte a borderless ───────────────────────────────

const CustomTable = Table.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            borderless: {
                default: false,
                parseHTML: el => el.classList.contains('table-borderless') || el.getAttribute('data-borderless') === 'true',
                renderHTML: attrs => attrs.borderless
                    ? { class: 'table-borderless', 'data-borderless': 'true' }
                    : { 'data-borderless': 'false' },
            },
        };
    },
});

// ─── Nó de logo arrastável ─────────────────────────────────────────────────────

function LogoNodeView({ node, updateAttributes, selected, editor }) {
    const { align, width, hideIfNoLogo } = node.attrs;
    const { logoUrl } = useContext(EditorExtrasContext);
    const containerRef = useRef(null);

    const justifyMap = { left: 'flex-start', center: 'center', right: 'flex-end' };

    const startResize = (side) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startW = containerRef.current?.offsetWidth || width || 150;
        const onMove = (ev) => {
            const dx = ev.clientX - startX;
            const delta = side === 'left' ? -dx : dx;
            const newW = Math.max(40, Math.min(2000, startW + delta));
            updateAttributes({ width: Math.round(newW) });
        };
        const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    const remove = () => editor?.chain().focus().deleteSelection().run();

    const handleSx = {
        position: 'absolute', width: 10, height: 10,
        bgcolor: '#1ABB9C', border: '2px solid #fff', borderRadius: '50%',
        zIndex: 10,
    };

    return (
        <NodeViewWrapper as="div" style={{ display: 'flex', justifyContent: justifyMap[align] || 'flex-start' }}>
            <Box
                ref={containerRef}
                data-drag-handle
                draggable
                sx={{
                    position: 'relative',
                    display: 'inline-block',
                    maxWidth: '100%',
                    width: width ? `${width}px` : 'auto',
                    border: selected ? '2px solid #1ABB9C' : '2px dashed #1ABB9C66',
                    borderRadius: 1,
                    cursor: selected ? 'grab' : 'default',
                    '&:hover': { borderColor: '#1ABB9C' },
                }}
            >
                {/* badge VARIÁVEL */}
                <Box sx={{
                    position: 'absolute', top: -10, left: 8,
                    bgcolor: '#1ABB9C', color: '#fff',
                    fontSize: '0.6rem', fontWeight: 700,
                    px: 0.7, py: 0.1, borderRadius: 0.5,
                    letterSpacing: 0.5, fontFamily: 'monospace',
                    zIndex: 9, pointerEvents: 'none',
                }}>
                    VARIÁVEL · {'{{LOGO_EMPRESA}}'}
                </Box>

                {logoUrl ? (
                    <Box component="img" src={logoUrl} alt="Logo" draggable={false}
                        sx={{ display: 'block', width: '100%', height: 'auto', maxHeight: 240, objectFit: 'contain', userSelect: 'none' }} />
                ) : (
                    <Box sx={{
                        width: '100%', height: 56,
                        bgcolor: '#f5f5f5',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexDirection: 'column', gap: 0.3,
                        userSelect: 'none',
                    }}>
                        <ImageIcon sx={{ color: '#bbb', fontSize: '1.4rem' }} />
                        <Typography variant="caption" sx={{ color: '#aaa', fontSize: '0.65rem', fontFamily: 'monospace' }}>
                            (logo não configurada)
                        </Typography>
                    </Box>
                )}

                {selected && (
                    <>
                        <Box onMouseDown={startResize('right')} sx={{ ...handleSx, right: -6, top: '50%', transform: 'translateY(-50%)', cursor: 'ew-resize' }} />
                        <Box onMouseDown={startResize('left')}  sx={{ ...handleSx, left: -6, top: '50%', transform: 'translateY(-50%)', cursor: 'ew-resize' }} />
                        <Box onMouseDown={startResize('right')} sx={{ ...handleSx, right: -6, bottom: -6, cursor: 'nwse-resize' }} />
                        <Box onMouseDown={startResize('left')}  sx={{ ...handleSx, left: -6, bottom: -6, cursor: 'nesw-resize' }} />

                        <Box sx={{
                            position: 'absolute', top: -42, left: '50%', transform: 'translateX(-50%)',
                            display: 'flex', alignItems: 'center', gap: 0.5,
                            bgcolor: '#fff', border: '1px solid #ddd', borderRadius: 1,
                            px: 0.75, py: 0.3, boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                            zIndex: 11, whiteSpace: 'nowrap',
                        }}>
                            <ToggleButtonGroup size="small" exclusive value={align || 'left'}
                                onChange={(_, v) => v && updateAttributes({ align: v })}
                                sx={{ '& .MuiToggleButton-root': { p: '2px 4px', border: 'none' } }}>
                                <ToggleButton value="left"><FormatAlignLeftIcon sx={{ fontSize: '0.9rem' }} /></ToggleButton>
                                <ToggleButton value="center"><FormatAlignCenterIcon sx={{ fontSize: '0.9rem' }} /></ToggleButton>
                                <ToggleButton value="right"><FormatAlignRightIcon sx={{ fontSize: '0.9rem' }} /></ToggleButton>
                            </ToggleButtonGroup>
                            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                            <Typography variant="caption" sx={{ color: '#666' }}>{width || 'auto'}px</Typography>
                            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                            <FormControlLabel
                                control={<Switch size="small" checked={!!hideIfNoLogo}
                                    onChange={e => updateAttributes({ hideIfNoLogo: e.target.checked })}
                                    sx={{ transform: 'scale(0.7)' }} />}
                                label={<Typography variant="caption" sx={{ color: '#555' }}>Ocultar se sem logo</Typography>}
                                sx={{ m: 0 }}
                            />
                            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                            <IconButton size="small" onClick={remove} sx={{ p: '2px', color: '#C62828' }}>
                                <RemoveIcon sx={{ fontSize: '0.9rem' }} />
                            </IconButton>
                        </Box>
                    </>
                )}
            </Box>
        </NodeViewWrapper>
    );
}

const LogoPlaceholder = Node.create({
    name: 'logoPlaceholder',
    group: 'block',
    atom: true,
    draggable: true,
    selectable: true,

    addAttributes() {
        return {
            align: { default: 'left' },
            width: { default: 150 },
            hideIfNoLogo: { default: false },
        };
    },

    parseHTML() {
        return [{ tag: 'div[data-logo-placeholder]' }];
    },

    renderHTML({ HTMLAttributes }) {
        const { align, width, hideIfNoLogo } = HTMLAttributes;
        return ['div', mergeAttributes({
            'data-logo-placeholder': '',
            'data-align': align || 'left',
            'data-width': String(width || 150),
            'data-hide-if-no-logo': hideIfNoLogo ? 'true' : 'false',
            style: `text-align:${align || 'left'}`,
            class: 'logo-placeholder',
        }), '{{LOGO_EMPRESA}}'];
    },

    addNodeView() {
        return ReactNodeViewRenderer(LogoNodeView);
    },

    addInputRules() {
        return [
            nodeInputRule({ find: /\{\{LOGO_EMPRESA\}\}$/, type: this.type }),
        ];
    },

    addPasteRules() {
        return [
            nodePasteRule({ find: /\{\{LOGO_EMPRESA\}\}/g, type: this.type }),
        ];
    },
});

// ─── Sub-componentes da toolbar ────────────────────────────────────────────────

function TableGridPicker({ onSelect }) {
    const [hovered, setHovered] = useState({ r: 0, c: 0 });

    return (
        <Box sx={{ p: 1.5 }}>
            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
                {hovered.r > 0 && hovered.c > 0
                    ? `${hovered.r} × ${hovered.c} tabela`
                    : 'Selecione o tamanho da tabela'}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${GRID_COLS}, 22px)`, gap: '3px' }}>
                {Array.from({ length: GRID_ROWS * GRID_COLS }).map((_, idx) => {
                    const r = Math.floor(idx / GRID_COLS) + 1;
                    const c = (idx % GRID_COLS) + 1;
                    const active = r <= hovered.r && c <= hovered.c;
                    return (
                        <Box
                            key={idx}
                            onMouseEnter={() => setHovered({ r, c })}
                            onClick={() => onSelect(r, c)}
                            sx={{
                                width: 20, height: 20,
                                border: '1px solid',
                                borderColor: active ? '#1ABB9C' : '#ccc',
                                bgcolor: active ? 'rgba(26,187,156,0.15)' : '#fafafa',
                                cursor: 'pointer',
                                borderRadius: '2px',
                                transition: 'all 0.1s',
                            }}
                        />
                    );
                })}
            </Box>
        </Box>
    );
}

function ToolbarBtn({ title, active, disabled, onClick, children }) {
    return (
        <Tooltip title={title} placement="top">
            <span>
                <IconButton
                    size="small"
                    onClick={onClick}
                    disabled={disabled}
                    sx={{
                        borderRadius: 1,
                        p: '4px',
                        bgcolor: active ? '#E0F7F3' : 'transparent',
                        color: active ? '#1ABB9C' : '#444',
                        '&:hover': { bgcolor: '#F0FDF9' },
                        '&.Mui-disabled': { color: '#ccc' },
                    }}
                >
                    {children}
                </IconButton>
            </span>
        </Tooltip>
    );
}

const selectSx = { height: 28, fontSize: '0.78rem' };

// ─── Componente principal ──────────────────────────────────────────────────────

/**
 * @param semMoldura remove borda e o esmaecido de somente-leitura. Para quando
 *                   o editor é usado apenas para renderizar conteúdo dentro de
 *                   outro contêiner que já tem a moldura (ex.: o papel do
 *                   preview no Gerador de Documentos).
 */
export default function RichTextEditor({ value, onChange, minHeight = 300, showLogoButton = false, readOnly = false, placeholder = 'Comece a digitar...', showVariables = false, pageLook = false, logoUrl = null, semMoldura = false }) {
    const [tableAnchor, setTableAnchor] = useState(null);
    const [varAnchor, setVarAnchor] = useState(null);
    const [linkDialog, setLinkDialog] = useState({ open: false, url: '' });
    const imageInputRef = useRef(null);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({ heading: { levels: [1, 2, 3, 4] } }),
            Underline,
            TextStyle,
            Color,
            FontFamily,
            FontSize,
            Highlight.configure({ multicolor: true }),
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            CustomTable.configure({ resizable: true }),
            TableRow,
            TableHeader,
            TableCell,
            LogoPlaceholder,
            ResizableImage.configure({ inline: false, allowBase64: true }),
            Link.configure({
                openOnClick: false,
                autolink: true,
                linkOnPaste: true,
                HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' },
            }),
            Placeholder.configure({ placeholder }),
            CharacterCount.configure({}),
        ],
        content: value || '',
        editable: !readOnly,
        onUpdate({ editor }) {
            onChange(editor.getHTML());
        },
    });

    React.useEffect(() => {
        if (editor) editor.setEditable(!readOnly);
    }, [editor, readOnly]);

    React.useEffect(() => {
        if (!editor) return;
        if (value !== editor.getHTML()) {
            editor.commands.setContent(value || '', false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value === '' || value == null ? value : null, editor]);

    const handleInsertTable = useCallback((rows, cols) => {
        setTableAnchor(null);
        editor?.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
    }, [editor]);

    const insertLogo = () => editor?.chain().focus().insertContent({ type: 'logoPlaceholder' }).run();

    const handleImageFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            const src = ev.target.result;
            editor?.chain().focus().setImage({ src }).run();
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const toggleTableBorder = () => {
        const isBorderless = editor?.getAttributes('table').borderless;
        editor?.chain().focus().updateAttributes('table', { borderless: !isBorderless }).run();
    };

    const clearFormatting = () => editor?.chain().focus().unsetAllMarks().clearNodes().run();

    const openLinkDialog = () => {
        const prev = editor?.getAttributes('link')?.href || '';
        setLinkDialog({ open: true, url: prev });
    };
    const applyLink = () => {
        const url = linkDialog.url.trim();
        setLinkDialog({ open: false, url: '' });
        if (!editor) return;
        if (!url) { editor.chain().focus().unsetLink().run(); return; }
        const normalized = /^https?:\/\//i.test(url) ? url : `https://${url}`;
        editor.chain().focus().extendMarkRange('link').setLink({ href: normalized }).run();
    };
    const removeLink = () => {
        setLinkDialog({ open: false, url: '' });
        editor?.chain().focus().unsetLink().run();
    };

    const insertVariavel = (v) => {
        setVarAnchor(null);
        editor?.chain().focus().insertContent(`{{${v}}}`).run();
    };

    const addColAfter = () => editor?.chain().focus().addColumnAfter().run();
    const addColBefore = () => editor?.chain().focus().addColumnBefore().run();
    const deleteCol = () => editor?.chain().focus().deleteColumn().run();
    const addRowAfter = () => editor?.chain().focus().addRowAfter().run();
    const addRowBefore = () => editor?.chain().focus().addRowBefore().run();
    const deleteRow = () => editor?.chain().focus().deleteRow().run();
    const deleteTable = () => editor?.chain().focus().deleteTable().run();
    const mergeCells = () => editor?.chain().focus().mergeCells().run();
    const splitCell = () => editor?.chain().focus().splitCell().run();
    const toggleHeaderRow = () => editor?.chain().focus().toggleHeaderRow().run();
    const toggleHeaderCol = () => editor?.chain().focus().toggleHeaderColumn().run();

    const isInTable = editor?.isActive('table');
    const isBorderless = editor?.getAttributes('table').borderless;

    const currentFontSize = editor?.getAttributes('textStyle').fontSize?.replace('pt', '') || '12';
    const currentFontFamily = editor?.getAttributes('textStyle').fontFamily || FONT_FAMILIES[0].value;
    const currentColor = editor?.getAttributes('textStyle').color || '#000000';
    const currentHighlight = editor?.getAttributes('highlight').color || 'none';

    const headingValue =
        editor?.isActive('heading', { level: 1 }) ? 'h1' :
        editor?.isActive('heading', { level: 2 }) ? 'h2' :
        editor?.isActive('heading', { level: 3 }) ? 'h3' :
        editor?.isActive('heading', { level: 4 }) ? 'h4' : 'p';

    return (
        <EditorExtrasContext.Provider value={{ logoUrl }}>
        <Box sx={{
            ...(semMoldura
                ? { border: 'none', borderRadius: 0 }
                : { border: '1px solid #ccc', borderRadius: 2, overflow: 'hidden' }),
            ...(!readOnly && { '&:focus-within': { borderColor: '#1ABB9C', boxShadow: '0 0 0 2px rgba(26,187,156,0.2)' } }),
            // O cinza-esmaecido sinaliza "campo desabilitado"; num preview que
            // simula o papel do documento ele só deixa o texto apagado.
            ...(readOnly && !semMoldura && { bgcolor: '#fafafa', opacity: 0.85 }),
        }}>
            {/* ── Toolbar ── */}
            {!readOnly && <Box sx={{
                display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5,
                p: '6px 10px', bgcolor: '#FAFAFA', borderBottom: '1px solid #e0e0e0',
                position: 'sticky', top: 0, zIndex: 5,
            }}>

                <ToolbarBtn title="Desfazer (Ctrl+Z)" onClick={() => editor?.chain().focus().undo().run()}>
                    <UndoIcon fontSize="small" />
                </ToolbarBtn>
                <ToolbarBtn title="Refazer (Ctrl+Y)" onClick={() => editor?.chain().focus().redo().run()}>
                    <RedoIcon fontSize="small" />
                </ToolbarBtn>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <Tooltip title="Estilo do parágrafo" placement="top">
                    <Select size="small" value={headingValue}
                        onChange={e => {
                            const v = e.target.value;
                            if (v === 'p') editor?.chain().focus().setParagraph().run();
                            else editor?.chain().focus().setHeading({ level: Number(v[1]) }).run();
                        }}
                        sx={{ ...selectSx, minWidth: 115 }}
                    >
                        <MenuItem value="p" sx={{ fontSize: '0.85rem' }}>Parágrafo</MenuItem>
                        <MenuItem value="h1" sx={{ fontSize: '1.4rem', fontWeight: 700, lineHeight: 1.2 }}>Título 1</MenuItem>
                        <MenuItem value="h2" sx={{ fontSize: '1.15rem', fontWeight: 700, lineHeight: 1.2 }}>Título 2</MenuItem>
                        <MenuItem value="h3" sx={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1.2 }}>Título 3</MenuItem>
                        <MenuItem value="h4" sx={{ fontSize: '0.9rem', fontWeight: 700, lineHeight: 1.2 }}>Título 4</MenuItem>
                    </Select>
                </Tooltip>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <Tooltip title="Família da fonte" placement="top">
                    <Select size="small" value={currentFontFamily}
                        onChange={e => editor?.chain().focus().setFontFamily(e.target.value).run()}
                        sx={{ ...selectSx, minWidth: 130 }}
                        renderValue={v => {
                            const found = FONT_FAMILIES.find(f => f.value === v);
                            return <span style={{ fontFamily: v, fontSize: '0.78rem' }}>{found?.label || 'Fonte'}</span>;
                        }}
                    >
                        {FONT_FAMILIES.map(f => (
                            <MenuItem key={f.value} value={f.value} sx={{ fontFamily: f.value, fontSize: '0.85rem' }}>{f.label}</MenuItem>
                        ))}
                    </Select>
                </Tooltip>

                <Tooltip title="Tamanho da fonte" placement="top">
                    <Select size="small" value={currentFontSize}
                        onChange={e => editor?.chain().focus().setFontSize(`${e.target.value}pt`).run()}
                        sx={{ ...selectSx, minWidth: 62 }}
                    >
                        {FONT_SIZES.map(s => (
                            <MenuItem key={s} value={s} sx={{ fontSize: '0.82rem' }}>{s}</MenuItem>
                        ))}
                    </Select>
                </Tooltip>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <ToolbarBtn title="Negrito (Ctrl+B)" active={editor?.isActive('bold')}
                    onClick={() => editor?.chain().focus().toggleBold().run()}>
                    <FormatBoldIcon fontSize="small" />
                </ToolbarBtn>
                <ToolbarBtn title="Itálico (Ctrl+I)" active={editor?.isActive('italic')}
                    onClick={() => editor?.chain().focus().toggleItalic().run()}>
                    <FormatItalicIcon fontSize="small" />
                </ToolbarBtn>
                <ToolbarBtn title="Sublinhado (Ctrl+U)" active={editor?.isActive('underline')}
                    onClick={() => editor?.chain().focus().toggleUnderline().run()}>
                    <FormatUnderlinedIcon fontSize="small" />
                </ToolbarBtn>
                <ToolbarBtn title="Tachado" active={editor?.isActive('strike')}
                    onClick={() => editor?.chain().focus().toggleStrike().run()}>
                    <StrikethroughSIcon fontSize="small" />
                </ToolbarBtn>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <Tooltip title="Cor do texto" placement="top">
                    <Select size="small" value={currentColor}
                        onChange={e => editor?.chain().focus().setColor(e.target.value).run()}
                        sx={{ ...selectSx, minWidth: 52 }}
                        renderValue={v => (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                <FormatColorTextIcon sx={{ fontSize: '1rem', color: v }} />
                                <Box sx={{ width: 16, height: 3, bgcolor: v, borderRadius: '1px' }} />
                            </Box>
                        )}
                    >
                        {COLORS.map(c => (
                            <MenuItem key={c.value} value={c.value}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 16, height: 16, borderRadius: '3px', bgcolor: c.value, border: '1px solid #ccc' }} />
                                    {c.label}
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </Tooltip>

                <Tooltip title="Destaque de texto" placement="top">
                    <Select size="small" value={currentHighlight}
                        onChange={e => {
                            if (e.target.value === 'none') editor?.chain().focus().unsetHighlight().run();
                            else editor?.chain().focus().setHighlight({ color: e.target.value }).run();
                        }}
                        sx={{ ...selectSx, minWidth: 52 }}
                        renderValue={v => (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                <BorderColorIcon sx={{ fontSize: '1rem', color: '#444' }} />
                                <Box sx={{ width: 16, height: 3, bgcolor: v === 'none' ? 'transparent' : v, border: v === 'none' ? '1px dashed #aaa' : 'none', borderRadius: '1px' }} />
                            </Box>
                        )}
                    >
                        {HIGHLIGHTS.map(h => (
                            <MenuItem key={h.value} value={h.value}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 16, height: 16, borderRadius: '3px', bgcolor: h.value === 'none' ? 'transparent' : h.value, border: '1px solid #ccc' }} />
                                    {h.label}
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </Tooltip>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <ToolbarBtn title="Alinhar à esquerda" active={editor?.isActive({ textAlign: 'left' })}
                    onClick={() => editor?.chain().focus().setTextAlign('left').run()}>
                    <FormatAlignLeftIcon fontSize="small" />
                </ToolbarBtn>
                <ToolbarBtn title="Centralizar" active={editor?.isActive({ textAlign: 'center' })}
                    onClick={() => editor?.chain().focus().setTextAlign('center').run()}>
                    <FormatAlignCenterIcon fontSize="small" />
                </ToolbarBtn>
                <ToolbarBtn title="Alinhar à direita" active={editor?.isActive({ textAlign: 'right' })}
                    onClick={() => editor?.chain().focus().setTextAlign('right').run()}>
                    <FormatAlignRightIcon fontSize="small" />
                </ToolbarBtn>
                <ToolbarBtn title="Justificar" active={editor?.isActive({ textAlign: 'justify' })}
                    onClick={() => editor?.chain().focus().setTextAlign('justify').run()}>
                    <FormatAlignJustifyIcon fontSize="small" />
                </ToolbarBtn>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <ToolbarBtn title="Lista com marcadores" active={editor?.isActive('bulletList')}
                    onClick={() => editor?.chain().focus().toggleBulletList().run()}>
                    <FormatListBulletedIcon fontSize="small" />
                </ToolbarBtn>
                <ToolbarBtn title="Lista numerada" active={editor?.isActive('orderedList')}
                    onClick={() => editor?.chain().focus().toggleOrderedList().run()}>
                    <FormatListNumberedIcon fontSize="small" />
                </ToolbarBtn>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <ToolbarBtn title={editor?.isActive('link') ? 'Editar link' : 'Inserir link'}
                    active={editor?.isActive('link')} onClick={openLinkDialog}>
                    <LinkIcon fontSize="small" />
                </ToolbarBtn>
                {editor?.isActive('link') && (
                    <ToolbarBtn title="Remover link" onClick={removeLink}>
                        <LinkOffIcon fontSize="small" />
                    </ToolbarBtn>
                )}

                <ToolbarBtn title="Limpar formatação" onClick={clearFormatting}>
                    <FormatClearIcon fontSize="small" />
                </ToolbarBtn>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                {/* Inserir imagem */}
                <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleImageFile}
                />
                <ToolbarBtn title="Inserir imagem" onClick={() => imageInputRef.current?.click()}>
                    <AddPhotoAlternateIcon fontSize="small" />
                </ToolbarBtn>

                {/* Inserir logo — só aparece se showLogoButton=true */}
                {showLogoButton && (
                    <ToolbarBtn title="Inserir placeholder da logo" onClick={insertLogo}>
                        <ImageIcon fontSize="small" />
                    </ToolbarBtn>
                )}

                {/* Inserir variável — só aparece se showVariables=true */}
                {showVariables && (
                    <>
                        <Tooltip title="Inserir variável" placement="top">
                            <span>
                                <IconButton size="small" onClick={e => setVarAnchor(e.currentTarget)}
                                    sx={{ borderRadius: 1, p: '4px', color: '#444', '&:hover': { bgcolor: '#F0FDF9' } }}>
                                    <DataObjectIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Popover
                            open={Boolean(varAnchor)}
                            anchorEl={varAnchor}
                            onClose={() => setVarAnchor(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            PaperProps={{ sx: { mt: 0.5, maxHeight: 420, width: 280 } }}
                        >
                            <Box sx={{ p: 1 }}>
                                {VARIAVEIS.map(g => (
                                    <Box key={g.grupo} sx={{ mb: 1 }}>
                                        <Typography variant="caption" sx={{ display: 'block', px: 1, py: 0.5, color: '#1ABB9C', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                            {g.grupo}
                                        </Typography>
                                        {g.itens.map(it => (
                                            <Box key={it.v}
                                                onClick={() => insertVariavel(it.v)}
                                                sx={{
                                                    px: 1, py: 0.5, borderRadius: 1, cursor: 'pointer',
                                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1,
                                                    '&:hover': { bgcolor: '#F0FDF9' },
                                                }}>
                                                <Typography variant="body2">{it.l}</Typography>
                                                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#888', fontSize: '0.7rem' }}>
                                                    {`{{${it.v}}}`}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                ))}
                            </Box>
                        </Popover>
                    </>
                )}

                {/* Inserir tabela — grid picker */}
                <Tooltip title="Inserir tabela" placement="top">
                    <span>
                        <IconButton size="small" onClick={e => setTableAnchor(e.currentTarget)}
                            sx={{ borderRadius: 1, p: '4px', color: '#444', '&:hover': { bgcolor: '#F0FDF9' } }}>
                            <TableChartIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Popover
                    open={Boolean(tableAnchor)}
                    anchorEl={tableAnchor}
                    onClose={() => setTableAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    PaperProps={{ sx: { mt: 0.5 } }}
                >
                    <TableGridPicker onSelect={handleInsertTable} />
                </Popover>

                {/* Controles de tabela */}
                {isInTable && (
                    <>
                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                        {/* Toggle borda */}
                        <ToolbarBtn
                            title={isBorderless ? 'Mostrar bordas' : 'Remover bordas'}
                            active={isBorderless}
                            onClick={toggleTableBorder}
                        >
                            {isBorderless ? <BorderAllIcon fontSize="small" /> : <BorderClearIcon fontSize="small" />}
                        </ToolbarBtn>

                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                        <ToolbarBtn title="Adicionar coluna antes" onClick={addColBefore}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <AddIcon sx={{ fontSize: '0.75rem' }} /><ViewColumnIcon fontSize="small" />
                            </Box>
                        </ToolbarBtn>
                        <ToolbarBtn title="Adicionar coluna depois" onClick={addColAfter}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <ViewColumnIcon fontSize="small" /><AddIcon sx={{ fontSize: '0.75rem' }} />
                            </Box>
                        </ToolbarBtn>
                        <ToolbarBtn title="Remover coluna" onClick={deleteCol}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <RemoveIcon sx={{ fontSize: '0.75rem' }} /><ViewColumnIcon fontSize="small" />
                            </Box>
                        </ToolbarBtn>

                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                        <ToolbarBtn title="Adicionar linha acima" onClick={addRowBefore}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                                <AddIcon sx={{ fontSize: '0.75rem' }} /><TableRowsIcon fontSize="small" />
                            </Box>
                        </ToolbarBtn>
                        <ToolbarBtn title="Adicionar linha abaixo" onClick={addRowAfter}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                                <TableRowsIcon fontSize="small" /><AddIcon sx={{ fontSize: '0.75rem' }} />
                            </Box>
                        </ToolbarBtn>
                        <ToolbarBtn title="Remover linha" onClick={deleteRow}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                                <RemoveIcon sx={{ fontSize: '0.75rem' }} /><TableRowsIcon fontSize="small" />
                            </Box>
                        </ToolbarBtn>

                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                        <ToolbarBtn title="Mesclar células" onClick={mergeCells}><CallMergeIcon fontSize="small" /></ToolbarBtn>
                        <ToolbarBtn title="Dividir célula" onClick={splitCell}><CallSplitIcon fontSize="small" /></ToolbarBtn>

                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                        <ToolbarBtn title="Alternar cabeçalho de linha" onClick={toggleHeaderRow}>
                            <Box sx={{ fontSize: '0.6rem', fontWeight: 700, lineHeight: 1, px: '2px' }}>H<br />row</Box>
                        </ToolbarBtn>
                        <ToolbarBtn title="Alternar cabeçalho de coluna" onClick={toggleHeaderCol}>
                            <Box sx={{ fontSize: '0.6rem', fontWeight: 700, lineHeight: 1, px: '2px' }}>H<br />col</Box>
                        </ToolbarBtn>

                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                        <Tooltip title="Remover tabela" placement="top">
                            <span>
                                <IconButton size="small" onClick={deleteTable}
                                    sx={{ p: '4px', borderRadius: 1, color: '#C62828', '&:hover': { bgcolor: '#FFEBEE' } }}>
                                    <TableChartIcon fontSize="small" />
                                    <RemoveIcon sx={{ fontSize: '0.75rem' }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </>
                )}
            </Box>}

            {/* ── Editor area ── */}
            <Box sx={{
                minHeight, maxHeight: 600, overflowY: 'auto',
                bgcolor: pageLook ? '#ECEFF1' : '#fff',
                px: pageLook ? 0 : 3,
                py: pageLook ? 3 : 2,
                '& > .ProseMirror': pageLook ? {
                    maxWidth: 794, mx: 'auto', bgcolor: '#fff',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                    borderRadius: 1, p: '40px 56px', minHeight: minHeight - 80,
                } : {},
                '& .ProseMirror': {
                    outline: 'none',
                    minHeight,
                    fontFamily: '"Times New Roman", Times, serif',
                    fontSize: '12pt',
                    lineHeight: 1.7,
                    color: '#222',
                    '& p': { margin: '0 0 8px 0' },
                    '& h1': { fontSize: '2rem', fontWeight: 700, lineHeight: 1.2, margin: '16px 0 10px 0', color: '#111' },
                    '& h2': { fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.25, margin: '14px 0 8px 0', color: '#1a1a1a' },
                    '& h3': { fontSize: '1.2rem', fontWeight: 600, lineHeight: 1.3, margin: '12px 0 6px 0', color: '#222' },
                    '& h4': { fontSize: '1rem', fontWeight: 600, lineHeight: 1.4, margin: '10px 0 4px 0', color: '#333' },
                    '& ul, & ol': { paddingLeft: '1.5rem', margin: '0 0 8px 0' },
                    '& mark': { borderRadius: '2px', padding: '0 1px' },

                    // Tabela normal
                    '& .tableWrapper': { width: '100%', overflowX: 'auto' },
                    '& table': {
                        borderCollapse: 'collapse',
                        width: '100% !important',
                        marginBottom: '12px',
                        tableLayout: 'fixed',
                    },
                    '& td, & th': {
                        border: '1px solid #bbb',
                        padding: '6px 10px',
                        minWidth: 40,
                        verticalAlign: 'top',
                        position: 'relative',
                        boxSizing: 'border-box',
                    },
                    '& th': { bgcolor: '#f5f5f5', fontWeight: 700 },

                    // Tabela sem borda
                    '& table.table-borderless, & table[data-borderless="true"]': {
                        '& td, & th': {
                            border: 'none !important',
                            background: 'transparent !important',
                        },
                        '& th': {
                            fontWeight: 700,
                        },
                    },

                    '& .selectedCell': { bgcolor: '#E0F7F3 !important', outline: '2px solid #1ABB9C' },
                    '& .column-resize-handle': {
                        position: 'absolute',
                        right: -2, top: 0, bottom: 0,
                        width: 4,
                        bgcolor: '#1ABB9C',
                        cursor: 'col-resize',
                        zIndex: 20,
                        pointerEvents: 'none',
                    },
                    '&.resize-cursor': { cursor: 'col-resize' },

                    '& img': { maxWidth: '100%', height: 'auto', display: 'block', cursor: 'default' },
                    '& img.ProseMirror-selectednode': { outline: '2px solid #1ABB9C' },

                    '& a': { color: '#1565C0', textDecoration: 'underline', cursor: 'pointer' },

                    '& p.is-editor-empty:first-of-type::before': {
                        content: 'attr(data-placeholder)',
                        color: '#aaa',
                        pointerEvents: 'none',
                        float: 'left',
                        height: 0,
                    },
                },
            }}>
                <EditorContent editor={editor} />
            </Box>

            {!readOnly && editor && (
                <Box sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    px: 2, py: 0.75, bgcolor: '#FAFAFA', borderTop: '1px solid #e0e0e0',
                    fontSize: '0.72rem', color: '#666',
                }}>
                    <Box>
                        {editor.storage.characterCount.words()} palavra(s)
                        {' • '}
                        {editor.storage.characterCount.characters()} caracter(es)
                    </Box>
                    <Box sx={{ color: '#999', fontStyle: 'italic' }}>
                        Ctrl+Z desfaz • Ctrl+Y refaz
                    </Box>
                </Box>
            )}

            {/* close provider container at bottom */}
            <Dialog open={linkDialog.open} onClose={() => setLinkDialog({ open: false, url: '' })} maxWidth="xs" fullWidth>
                <DialogTitle>{editor?.isActive('link') ? 'Editar link' : 'Inserir link'}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        margin="dense"
                        label="URL"
                        placeholder="https://exemplo.com"
                        value={linkDialog.url}
                        onChange={e => setLinkDialog(prev => ({ ...prev, url: e.target.value }))}
                        onKeyDown={e => { if (e.key === 'Enter') applyLink(); }}
                    />
                </DialogContent>
                <DialogActions>
                    {editor?.isActive('link') && (
                        <Button onClick={removeLink} color="error">Remover</Button>
                    )}
                    <Button onClick={() => setLinkDialog({ open: false, url: '' })}>Cancelar</Button>
                    <Button onClick={applyLink} variant="contained">Aplicar</Button>
                </DialogActions>
            </Dialog>
        </Box>
        </EditorExtrasContext.Provider>
    );
}
